import type { AttachDestination, AttachMediaMode, AttachTarget, MetaAdDraftInput, MetaCallToAction, UtmParams } from '../../types/metaAdDraft';
import type { LiveAd, LiveAdSet, LiveCampaignDetail } from '../../types/metaLiveCampaigns';
import { emptyMetaAdDraftInput } from './emptyDraft';
import { isFbMePlaceholder, modeIsAvailable, reusableMedia } from '../../lib/metaAttachMedia';

// "Add an ad to a live ad set": pure helpers for the picker and the prefill. No I/O.
// The composer starts from what Zernio could READ (primary text, link, identity) and
// asks the AM for what it cannot (headline, description, call to action, media).

export const NOT_READABLE_NOTE = 'Not readable from Meta: enter it';
export const PRIMARY_TEXT_NOTE = 'Not readable from Meta for this ad. Enter the primary text.';
export const PAUSED_LINE = "Active starts spending inside this ad set's existing budget straight away.";
export const NEW_ASSET_SUFFIX = ' – new asset';

export function destinationLabel(d: AttachDestination): string {
  return d === 'website' ? 'Website' : d === 'instant_form' ? 'Instant form' : 'Unknown';
}

/** Website → Learn more, Instant form → Sign up; unknown follows the website default. */
export function defaultCallToAction(d: AttachDestination): MetaCallToAction {
  return d === 'instant_form' ? 'SIGN_UP' : 'LEARN_MORE';
}

export function adNameFromSource(sourceName: string | null | undefined): string {
  const base = (sourceName ?? '').trim();
  return base ? `${base}${NEW_ASSET_SUFFIX}` : '';
}

/** First non-empty line, for the "Start from an ad" cards. */
export function firstLine(text: string | null | undefined, max = 90): string {
  const line = (text ?? '').split(/\r?\n/).map((l) => l.trim()).find((l) => l !== '') ?? '';
  return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line;
}

export function buildAttachTarget(campaign: LiveCampaignDetail, adSet: LiveAdSet, source: LiveAd | null): AttachTarget {
  return {
    campaignId: campaign.id,
    campaignName: campaign.name,
    adSetId: adSet.id,
    adSetName: adSet.name,
    adSetStatus: adSet.metaStatus,
    destination: adSet.destination,
    sourceAdId: source?.id ?? null,
    sourceAdName: source?.name ?? null,
    pageId: source?.pageId ?? null,
    instagramUserId: source?.instagramUserId ?? null,
    sourceCampaignId: source ? campaign.id : null,
    sourceCreativeId: source?.creativeId ?? null,
    sourceImageHash: source?.imageHash ?? null,
    sourceVideoId: source?.videoId ?? null,
    sourceThumbnailUrl: source?.fullImageUrl ?? source?.thumbnailUrl ?? null,
    mediaMode: 'new',
  };
}

/** Texts the source ad carries beyond the first of each, for the "Other texts from the source" note. */
export function otherSourceTexts(source: LiveAd | null): { primaryTexts: string[]; headlines: string[]; descriptions: string[] } {
  return {
    primaryTexts: (source?.bodies ?? []).slice(1),
    headlines: (source?.headlines ?? []).slice(1),
    descriptions: (source?.descriptions ?? []).slice(1),
  };
}

/** The media choice an AM sees, from what the source ad offers. 'original' needs a hash or video id; 'exact' a creative id. */
export function mediaChoiceFor(target: AttachTarget): { original: 'image' | 'video' | null; exact: boolean } {
  return reusableMedia(target);
}

/**
 * The draft the composer opens. Campaign / ad set parts are inert placeholders (never
 * sent). Media is ALWAYS empty: a thumbnail from Meta is never re-used as creative.
 * Status defaults to PAUSED.
 */
export function buildAttachDraftInput(args: {
  accountId: string;
  campaign: LiveCampaignDetail;
  adSet: LiveAdSet;
  source: LiveAd | null;
}): MetaAdDraftInput {
  const { accountId, campaign, adSet, source } = args;
  const input = emptyMetaAdDraftInput();
  const dest: AttachDestination = adSet.destination;
  const name = adNameFromSource(source?.name);

  input.title = `Added to ${adSet.name || 'ad set'}`;
  input.accountId = accountId;
  input.attachTarget = buildAttachTarget(campaign, adSet, source);

  input.ads.name = name;
  input.ads.status = 'PAUSED';
  input.ads.instagramAccountId = source?.instagramUserId ?? null;
  input.ads.destination =
    dest === 'instant_form'
      ? { type: 'instant_form', leadGenFormId: source?.leadGenFormId ?? '', leadGenFormName: '' }
      : { type: 'website', url: isFbMePlaceholder(source?.linkUrl) ? '' : (source?.linkUrl ?? '') };
  // Read from Meta when Zernio returned it; the defaults are only for what it did not.
  input.ads.creative.callToAction = readCallToAction(source?.callToAction) ?? defaultCallToAction(dest);
  input.ads.creative.primaryTexts = [source?.primaryText ?? ''];
  input.ads.creative.headlines = [source?.headlines?.[0] ?? ''];
  input.ads.creative.descriptions = [source?.descriptions?.[0] ?? ''];
  const tags = parseUrlTags(source?.urlTags);
  if (tags) input.ads.utmParams = { ...input.ads.utmParams, ...tags };
  // Meta's pixel / UTM tags are not readable; attach sends no pixel and plain UTM defaults.
  input.ads.pixelId = null;
  return input;
}

const CTA_VALUES: ReadonlySet<string> = new Set<MetaCallToAction>([
  'LEARN_MORE',
  'SHOP_NOW',
  'SIGN_UP',
  'BOOK_TRAVEL',
  'CONTACT_US',
  'DOWNLOAD',
  'GET_OFFER',
  'GET_QUOTE',
  'SUBSCRIBE',
  'WATCH_MORE',
]);

/** A call to action from Meta, only if it is one Zernio accepts; otherwise null (we do not guess). */
export function readCallToAction(v: string | null | undefined): MetaCallToAction | null {
  const k = String(v ?? '').trim().toUpperCase();
  return CTA_VALUES.has(k) ? (k as MetaCallToAction) : null;
}

/** "utm_source=facebook&utm_medium=paid" -> the tags we model; null when none are present. */
export function parseUrlTags(tags: string | null | undefined): Partial<UtmParams> | null {
  if (!tags) return null;
  const out: Partial<UtmParams> = {};
  for (const part of tags.replace(/^\?/, '').split('&')) {
    const [k, ...rest] = part.split('=');
    let v = rest.join('=');
    try {
      v = decodeURIComponent(v);
    } catch {
      /* keep the raw value */
    }
    v = v.trim();
    const key = (k ?? '').trim().toLowerCase();
    if (!v) continue;
    if (key === 'utm_source') out.source = v;
    else if (key === 'utm_medium') out.medium = v;
    else if (key === 'utm_campaign') out.campaign = v;
    else if (key === 'utm_content') out.content = v;
    else if (key === 'utm_term') out.term = v;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/** Which prefill fields Meta did NOT give us for this ad, in the AM's words. */
export function missingPrefillFields(source: LiveAd | null, dest: AttachDestination): string[] {
  if (!source) return [];
  const out: string[] = [];
  if (!source.headlines?.length) out.push('headline');
  if (!source.primaryText) out.push('primary text');
  if (!source.descriptions?.length) out.push('description');
  if (!readCallToAction(source.callToAction)) out.push('call to action');
  if (dest === 'instant_form' && !source.leadGenFormId) out.push('lead form');
  if (dest !== 'instant_form' && !source.linkUrl) out.push('link');
  return out;
}

export function destinationIsLocked(d: AttachDestination): boolean {
  return d !== 'unknown';
}

export const COPY_SUFFIX = ' – copy';

/**
 * Switch the draft's media mode (Part A). Only a mode the source ad actually supports is applied.
 * 'original' follows the source's kind (video id -> video, image hash -> image); 'exact' renames
 * the ad "<source> – copy" because nothing about the creative can differ.
 */
export function withMediaMode(input: MetaAdDraftInput, mode: AttachMediaMode): MetaAdDraftInput {
  const target = input.attachTarget;
  if (!target || !modeIsAvailable(mode, target)) return input;
  const next: MetaAdDraftInput = { ...input, attachTarget: { ...target, mediaMode: mode } };
  if (mode === 'original') {
    const kind = reusableMedia(target).original;
    if (kind) next.ads = { ...next.ads, creative: { ...next.ads.creative, assetType: kind } };
  }
  if (mode === 'exact' && target.sourceAdName) {
    next.ads = { ...next.ads, name: `${target.sourceAdName.trim()}${COPY_SUFFIX}` };
  }
  return next;
}

/** Ads Manager's campaign view for one campaign (the copy a duplicate made). */
export function adsManagerCampaignUrl(adAccountId: string | null, campaignId: string | null): string | null {
  if (!adAccountId || !campaignId) return null;
  const act = adAccountId.replace(/^act_/, '');
  return `https://business.facebook.com/adsmanager/manage/campaigns?act=${encodeURIComponent(act)}&selected_campaign_ids=${encodeURIComponent(campaignId)}`;
}

/** "3 ad sets, 12 ads" */
export function describeCounts(c: { adSets: number; ads: number } | null): string {
  if (!c) return 'could not be read back';
  return `${c.adSets} ad set${c.adSets === 1 ? '' : 's'}, ${c.ads} ad${c.ads === 1 ? '' : 's'}`;
}
